import { Fragment, useState, useId, MouseEvent, FC } from "react";
import { Button, IconButton, SvgIcon, Menu, MenuItem } from "@mui/material";

import "./style.scss";
import { ReactComponent as GearIcon } from "../../assets/gear.icon.svg";
import { ReactComponent as MoreIcon } from "../../assets/more-vertical.icon.svg";

export enum SettingMenuType {
    default,
    more
}

export enum SettingItemType {
    default,
    dangerous
}

export interface SettingItem {
    icon?: FC<React.SVGProps<SVGSVGElement>>;
    name: string;
    type: SettingItemType;
    handler: () => void;
}

export const SettingMenu = ({
    actions,
    type = SettingMenuType.default,
    className = "",
    ariaLabel = "Open menu"
}: {
    actions: SettingItem[];
    type?: SettingMenuType;
    className?: string;
    ariaLabel?: string;
}) => {
    const triggerId = useId();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = (handler: () => void) => {
        // Run the action (which may open a dialog) while the menu is still open,
        // then close the menu. Dialogs restore focus to the menu's trigger via
        // the menu's aria-labelledby (see ModalTemplate/ConfirmationPopup), so
        // focus returns to the kebab — not the vanishing menu item — on close.
        handler();
        handleMenuClose();
    };

    return (
        <Fragment>
            <IconButton
                id={triggerId}
                className={`${type === SettingMenuType.more ? "more-menu-icon-button" : "setting-menu-icon-button"} ${className}`}
                aria-label={ariaLabel}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleMenuClick}
            >
                <SvgIcon
                    component={type === SettingMenuType.more ? MoreIcon : GearIcon}
                    inheritViewBox
                />
            </IconButton>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "setting-dropdown-menu"
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "center" }}
            >
                {actions.map(action => (
                    <MenuItem
                        key={action.name}
                        className={`setting-dropdown-menu-item${action.type == SettingItemType.dangerous ? " setting-dropdown-menu-item--dangerous" : ""}`}
                        onClick={() => handleMenuItem(action.handler)}
                    >
                        {action.icon && (
                            <SvgIcon
                                className="setting-dropdown-menu-item__icon"
                                component={action.icon}
                                inheritViewBox
                            />
                        )}
                        <span className="setting-dropdown-menu-item__title">{action.name}</span>
                    </MenuItem>
                ))}
            </Menu>
        </Fragment>
    );
};

export interface DropdownMenuItem {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    handler: () => void;
    // When true, this item remains visible on mobile devices. See
    // filterMobileSupported in platform.util.
    mobileSupported?: boolean;
}

export const DropdownMenu = ({
    icon,
    title,
    actions,
    ariaLabel = "Open menu"
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title?: string;
    actions: DropdownMenuItem[];
    ariaLabel?: string;
}) => {
    const triggerId = useId();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = (handler: () => void) => {
        // Run the action (which may open a dialog) while the menu is still open,
        // then close the menu. Dialogs restore focus to the menu's trigger via
        // the menu's aria-labelledby (see ModalTemplate/ConfirmationPopup), so
        // focus returns to the kebab — not the vanishing menu item — on close.
        handler();
        handleMenuClose();
    };

    return (
        <Fragment>
            <Button
                id={triggerId}
                className="dropdown-menu-button"
                aria-label={title ? undefined : ariaLabel}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleMenuClick}
            >
                <SvgIcon className="dropdown-menu-button__icon" component={icon} inheritViewBox />
                {title && <span className="dropdown-menu-button__title">{title}</span>}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "setting-dropdown-menu"
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "center" }}
                anchorOrigin={{ horizontal: "right", vertical: "center" }}
            >
                {actions.map(action => (
                    <MenuItem
                        key={action.title}
                        className="setting-dropdown-menu-item"
                        onClick={() => handleMenuItem(action.handler)}
                    >
                        <SvgIcon
                            className="setting-dropdown-menu-item__icon"
                            component={action.icon}
                            inheritViewBox
                        />
                        <span className="setting-dropdown-menu-item__title">{action.title}</span>
                    </MenuItem>
                ))}
            </Menu>
        </Fragment>
    );
};
